// Centralized client for the Priism v2 public booking + patient API.
//
// priism-appt is multi-tenant. The tenant_code arrives as `referenceCode`
// (the URL route param + localStorage) — the linking client website passes
// the v2 tenant_code directly (e.g. /home/manoshanti_bengaluru). v2 resolves
// the tenant from ?tenant=<referenceCode>, so the same build serves every
// clinic with nothing hardcoded.
//
// Each function returns data in the legacy shapes the components already
// consume, so migrating a call site is a one-line swap (axios → v2.fn).

import axios from "axios";
import Utils from "../utils/utils";

// Single edge: the priism-appt frontend talks ONLY to the priism-appt backend.
// The backend OWNS patient auth (OTP request/verify/login in the appt DB) and
// proxies the data calls (clinicians / services / slots / countries / order +
// appointment creation) to priism-v2. So patient OTP is handled consistently in
// one place (matching the manoshanti flow) instead of the frontend hitting v2
// directly. Strip the trailing /v1 — the call paths already include /v1/public/.
function base(): string {
  return Utils.PRIISM_APPT_API_ENDPOINT.replace(/\/v1\/?$/, "");
}

export function currentTenant(): string {
  return localStorage.getItem("referenceCode") || "";
}

function url(path: string, tenant?: string): string {
  const t = tenant || currentTenant();
  const sep = path.includes("?") ? "&" : "?";
  return `${base()}${path}${t ? `${sep}tenant=${encodeURIComponent(t)}` : ""}`;
}

// ── mode mapping (legacy "Online"/"In-person" ↔ v2 "tele"/"in_person") ──
const MODE_TO_LEGACY: Record<string, string> = { in_person: "In-person", tele: "Online" };
function modeFromLegacy(m: string | undefined): "tele" | "in_person" {
  const low = (m || "").toLowerCase();
  return low === "online" || low.includes("online") || low.includes("tele") || low.includes("video")
    ? "tele"
    : "in_person";
}

function splitName(full: string): { first: string; last: string } {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
}

// ── v2 → legacy shape adapters ──

function v2ClinicianToLegacy(c: any): any {
  const { first, last } = splitName(c.full_name || "");
  const services: any[] = c.services || [];
  const sessionTypes = Array.from(
    new Set(
      services.flatMap((s) => (s.modes || []).map((m: string) => MODE_TO_LEGACY[m]).filter(Boolean)),
    ),
  );
  const billingServices = services.map((s) => ({
    serviceId: s.id,
    name: s.name,
    duration: s.duration_min,
    default_bill_amount: s.amount,
    sessionType: (s.modes || []).map((m: string) => MODE_TO_LEGACY[m]).filter(Boolean),
    isWebsiteService: true,
    is_deleted: false,
  }));
  const priced = services.filter((s) => Number(s.amount) > 0);
  const cheapest = priced.reduce<any>((b, s) => (!b || Number(s.amount) < Number(b.amount) ? s : b), null);
  return {
    userId: c.id,
    // Display priority (lower = higher up); null sorts last. Drives clinician
    // list order, including across an aggregated booking group.
    priority: c.priority ?? null,
    // c.tenant is the clinician's BRANCH tenant (set when the request code is a
    // multi-branch booking group); falls back to the request tenant for a
    // single-tenant clinic. All per-doctor calls (slots/order/auth) use this.
    heId: c.tenant || currentTenant(),
    firstName: first,
    lastName: last,
    prefix: "",
    designation: c.designation ? [c.designation] : [],
    qualification: c.qualifications || "",
    expertise: c.expertise || [],
    languagesISpeak: c.languages || [],
    aboutMe: c.about || "",
    profilePicture: c.profile_image_url || null,
    sessionTypes,
    billingServices,
    billAmtStartsAt: cheapest
      ? { amount: cheapest.amount, duration: cheapest.duration_min }
      : { amount: null, duration: null },
    // v2 doesn't expose a per-clinician location; clinics are picked separately.
    location: null,
    opTimings: {},
    teleTimings: {},
  };
}

function v2LocationToLegacy(l: any): any {
  return {
    locId: l.id,
    heId: currentTenant(),
    name: l.name,
    address: null,
    city: l.city ?? null,
    state: null,
    pin: null,
    phone1: null,
  };
}

function v2PatientToLegacy(data: any): any {
  const p = data?.patient ?? {};
  const { first, last } = splitName(p.full_name || "");
  return {
    id: p.id,
    patient_id: p.id,
    patientId: p.id,
    patient_no: p.uh_id ?? null,
    patientNo: p.uh_id ?? null,
    first_name: first,
    firstName: first,
    last_name: last,
    lastName: last,
    email: p.email ?? null,
    mobile: p.mobile ?? null,
    country_code: p.mobile_country_code ?? null,
    sex: p.sex ?? null,
    dob: p.date_of_birth ?? null,
    age: null,
    heid: currentTenant(),
    access_token: data?.access_token ?? "",
  };
}

// HH:mm (24h) from an ISO/naive datetime → "hh:mm A".
function formatTime12h(iso: string): string {
  const m = /T(\d{2}):(\d{2})/.exec(iso || "");
  if (!m) return "";
  const h = parseInt(m[1], 10);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${m[2]} ${ap}`;
}

// v2 patient-appointment → the legacy row shape the appointment-list table renders
// (apptId / start / startTime / modeOfAdmission / locName / doctor / status).
function v2ApptToLegacyRow(a: any): any {
  const { first, last } = splitName(a.clinician_name || "");
  return {
    apptId: a.id,
    start: a.scheduled_at,
    startTime: formatTime12h(a.scheduled_at),
    modeOfAdmission: a.mode === "tele" ? "Online" : "In-person",
    locName: a.location_name || "-",
    doctor: { firstName: first, lastName: last },
    status: a.status,
    heId: currentTenant(),
    service_name: a.service_name,
    duration: a.duration_min,
  };
}

// v2 patient-visit → the legacy visit-row shape (visitId / visitDate / diagnosis[]).
function v2VisitToLegacyRow(v: any): any {
  // Diagnosis names: prefer the structured list, fall back to the denormalised
  // summary string. The table renders `diagnosis.map(d => d.diagnosisName)`.
  const names: string[] =
    Array.isArray(v.diagnoses) && v.diagnoses.length
      ? v.diagnoses
      : v.diagnosis_summary
      ? [v.diagnosis_summary]
      : [];
  return {
    visitId: v.id,
    visitDate: v.visit_date,
    // v2 mode is in_person / tele; show the legacy human label.
    modeOfAdmission:
      v.mode === "tele"
        ? "Online"
        : v.mode === "in_person"
        ? "In-person"
        : v.mode || "",
    diagnosis: names.map((n) => ({ diagnosisName: n })),
  };
}

// Short-lived OTP proof, set by verifyOtp and consumed by login/signup.
let lastVerificationToken: string | null = null;

type OtpPurpose = "LOGIN" | "SIGN-UP" | "SIGNUP";
function v2Purpose(p: OtpPurpose): "login" | "signup" {
  return p === "LOGIN" ? "login" : "signup";
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const v2 = {
  // ── Discovery ──
  async getDoctors(tenant?: string): Promise<any[]> {
    const res = await axios.get(url(`/v1/public/booking/clinicians`, tenant));
    // Priority asc (unset last), then alphabetical. The backend already orders a
    // single branch this way; re-apply on the merged booking-group list so the
    // whole roster reads as one priority-ordered list.
    const rows = (res.data || []).slice().sort((a: any, b: any) => {
      const na = a?.priority == null, nb = b?.priority == null;
      if (na !== nb) return na ? 1 : -1;
      if (!na && a.priority !== b.priority) return a.priority - b.priority;
      return String(a?.full_name || "").localeCompare(String(b?.full_name || ""));
    });
    return rows.map(v2ClinicianToLegacy);
  },

  async getDoctor(userId: string, tenant?: string): Promise<any | null> {
    const list = await this.getDoctors(tenant);
    return list.find((d) => d.userId === userId) || null;
  },

  async getLocations(tenant?: string): Promise<any[]> {
    const res = await axios.get(url(`/v1/public/booking/locations`, tenant));
    return (res.data || []).map(v2LocationToLegacy);
  },

  async getHealthEst(tenant?: string): Promise<{ isPaymentGatewayIntegrated: boolean; allowSameDayBooking: boolean }> {
    const res = await axios.get(url(`/v1/public/booking/payment-config`, tenant));
    // Honour the tenant's website preference (default true if absent).
    return { isPaymentGatewayIntegrated: !!res.data?.enabled, allowSameDayBooking: res.data?.allow_same_day_booking !== false };
  },

  async getPaymentConfig(tenant?: string): Promise<{ razorpay_key_id: string | null; brand_name: string | null; currency: string; web_booking_enabled: boolean; allow_same_day_booking: boolean }> {
    const res = await axios.get(url(`/v1/public/booking/payment-config`, tenant));
    return {
      razorpay_key_id: res.data?.key_id || null,
      brand_name: res.data?.brand_name || null,
      currency: res.data?.currency || "INR",
      // Whether online web booking is turned on (+ messaging configured) for this
      // tenant/group. The app gates itself on this at load.
      web_booking_enabled: res.data?.web_booking_enabled === true,
      // Website preference: allow today's date (default true when absent).
      allow_same_day_booking: res.data?.allow_same_day_booking !== false,
    };
  },

  // Lightweight on-load gate: is web booking enabled for this tenant/group code?
  // Defaults to TRUE on any error so a transient backend hiccup never hard-blocks
  // a clinic that's actually enabled (the booking calls themselves still enforce).
  async isWebBookingEnabled(tenant?: string): Promise<boolean> {
    try {
      const cfg = await this.getPaymentConfig(tenant);
      return cfg.web_booking_enabled;
    } catch {
      return true;
    }
  },

  // Country list for the mobile picker — fetched from v2's /v1/public/countries,
  // which already returns the { name, code, countryCode, mobile_length } shape the
  // pickers consume. Falls back to a builtin India entry if the request fails.
  async getCountries(): Promise<Array<{ name: string; code: string; countryCode: string; mobile_length: string }>> {
    const fallback = [{ name: "India", code: "IN", countryCode: "+91", mobile_length: "10" }];
    try {
      const res = await axios.get(`${base()}/v1/public/countries`);
      const data = Array.isArray(res.data) ? res.data : [];
      if (!data.length) return fallback;
      return data.map((c: any) => ({
        name: c.name,
        code: c.code,
        countryCode: c.countryCode,
        // v2 returns mobile_length (snake); tolerate mobileLength (camel) too.
        mobile_length: String(c.mobile_length ?? c.mobileLength ?? ""),
      }));
    } catch {
      return fallback;
    }
  },

  // ── Slots ── returns the legacy { [serviceId]: slot[] } shape.
  async getSlots(args: {
    tenant?: string;
    doctorId: string;
    locationId: string | null;
    mode: string;
    serviceId: string;
    date: string;
  }): Promise<Record<string, any[]>> {
    const mode = modeFromLegacy(args.mode);
    const res = await axios.post(url(`/v1/public/booking/slots`, args.tenant), {
      date: args.date,
      mode,
      clinician_id: args.doctorId,
      location_id: mode === "in_person" ? args.locationId : null,
      service_id: args.serviceId,
    });
    const slots = (res.data?.slots || []).map((s: any) => ({
      start: s.start,
      end: s.end,
      isBooked: s.available === false,
      isBlocked: false,
      blockInWebsite: false,
    }));
    return { [args.serviceId]: slots };
  },

  // ── Patient auth (OTP) ──
  async sendOtp(args: {
    tenant?: string;
    countryCode: string;
    mobile: string;
    email?: string;
    purpose: OtpPurpose;
  }): Promise<{ statusCode: number; hasAccount: boolean; sent: boolean }> {
    const res = await axios.post(url(`/v1/public/patient/otp/request`, args.tenant), {
      purpose: v2Purpose(args.purpose),
      mobile_country_code: args.countryCode,
      mobile: `${args.countryCode}${args.mobile}`,
      email: args.email || undefined,
      channel: args.email ? "both" : "sms",
    });
    // sent=false means no OTP actually went out (clinic messaging not configured
    // / delivery service unreachable). Surface it so callers can warn the patient.
    return { statusCode: 200, hasAccount: !!res.data?.has_account, sent: res.data?.sent !== false };
  },

  async verifyOtp(args: {
    tenant?: string;
    countryCode: string;
    mobile: string;
    email?: string;
    otp: string;
    purpose: OtpPurpose;
  }): Promise<{ statusCode: number; status: string }> {
    const res = await axios.post(url(`/v1/public/patient/otp/verify`, args.tenant), {
      purpose: v2Purpose(args.purpose),
      mobile: `${args.countryCode}${args.mobile}`,
      email: args.email || undefined,
      otp: args.otp,
    });
    lastVerificationToken = res.data?.verification_token || null;
    return { statusCode: 200, status: "VERIFIED" };
  },

  // One phone number reaches a whole family, so the backend may answer with the
  // people it found instead of a session. `supports_selection` opts in to that
  // — without it the server signs you in as whichever patient it saw first, and
  // the portal would show one relative's records under another's name.
  //
  // The verification token is deliberately NOT spent on that branch: it is what
  // authorises the follow-up `selectPatient` call.
  async login(args: { tenant?: string }): Promise<any> {
    if (!lastVerificationToken) throw new Error("verify_first");
    const res = await axios.post(url(`/v1/public/patient/login`, args.tenant), {
      verification_token: lastVerificationToken,
      supports_selection: true,
    });
    if (res.data?.needs_selection) {
      return { needsSelection: true, patients: res.data?.patients ?? [] };
    }
    lastVerificationToken = null;
    return v2PatientToLegacy(res.data);
  },

  // Finish a login the picker interrupted. The chosen id is re-checked against
  // the OTP-verified number server-side, so nothing here is trusted on its own.
  async selectPatient(args: { tenant?: string; patientId: string }): Promise<any> {
    if (!lastVerificationToken) throw new Error("verify_first");
    const res = await axios.post(url(`/v1/public/patient/select-patient`, args.tenant), {
      verification_token: lastVerificationToken,
      patient_id: args.patientId,
    });
    lastVerificationToken = null;
    return v2PatientToLegacy(res.data);
  },

  async signup(args: {
    tenant?: string;
    firstName: string;
    lastName?: string;
    sex?: string;
    age?: number;
    email?: string;
  }): Promise<any> {
    if (!lastVerificationToken) throw new Error("verify_first");
    const res = await axios.post(url(`/v1/public/patient/signup`, args.tenant), {
      verification_token: lastVerificationToken,
      full_name: [args.firstName, args.lastName].filter((s) => s && s.trim()).join(" "),
      sex: args.sex || "O",
      email: args.email || undefined,
      age: args.age ?? undefined,
    });
    lastVerificationToken = null;
    return v2PatientToLegacy(res.data);
  },

  // ── Pay-at-booking ──
  // Creates a Razorpay order from the booking intent; the appointment is
  // created server-side on the payment.captured webhook.
  async createOrder(args: {
    tenant?: string;
    doctor: any;
    service: { serviceId: string };
    mode: string;
    date: string;       // YYYY-MM-DD
    startTime: string;  // HH:mm (24h)
    locationId: string | null;
    visitType?: "first" | "followup";
    // Sent true after the patient confirms a duplicate-service booking — skips
    // the server's "you already have an upcoming appointment" 409 guard.
    allowDuplicate?: boolean;
    patient: { name: string; sex?: string; countryCode?: string; mobile: string; email?: string };
  }): Promise<{ order: { id: string; amount?: number; currency?: string; status: string } }> {
    const mode = modeFromLegacy(args.mode);
    const res = await axios.post(url(`/v1/public/booking/orders`, args.tenant), {
      clinician_id: args.doctor.userId,
      service_id: args.service.serviceId,
      location_id: mode === "in_person" ? args.locationId : null,
      mode,
      scheduled_at: `${args.date}T${args.startTime}:00`,
      visit_type: args.visitType || "first",
      allow_duplicate: args.allowDuplicate === true,
      full_name: args.patient.name,
      sex: (args.patient.sex as any) || "O",
      mobile_country_code: args.patient.countryCode || "+91",
      mobile: args.patient.mobile,
      email: args.patient.email || undefined,
    });
    return {
      order: {
        id: res.data?.order_id,
        amount: res.data?.amount,
        currency: res.data?.currency,
        status: "created",
      },
    };
  },

  // Direct booking (no online payment) — creates the appointment immediately.
  // Used by clinics without a payment gateway (pay at the clinic).
  async createAppointment(args: {
    tenant?: string;
    doctor: any;
    service: { serviceId: string };
    mode: string;
    date: string;       // YYYY-MM-DD
    startTime: string;  // HH:mm (24h)
    locationId: string | null;
    visitType?: "first" | "followup";
    patient: { name: string; sex?: string; countryCode?: string; mobile: string; email?: string };
  }): Promise<any> {
    const mode = modeFromLegacy(args.mode);
    const res = await axios.post(url(`/v1/public/booking/appointments`, args.tenant), {
      clinician_id: args.doctor.userId,
      service_id: args.service.serviceId,
      location_id: mode === "in_person" ? args.locationId : null,
      mode,
      scheduled_at: `${args.date}T${args.startTime}:00`,
      visit_type: args.visitType || "first",
      full_name: args.patient.name,
      sex: (args.patient.sex as any) || "O",
      mobile_country_code: args.patient.countryCode || "+91",
      mobile: args.patient.mobile,
      email: args.patient.email || undefined,
    });
    return res.data;
  },

  // ── Patient self-service (authed by the v2 patient JWT in localStorage) ──
  async getMyAppointments(scope: "upcoming" | "history" | "all", tenant?: string): Promise<any[]> {
    const res = await axios.get(url(`/v1/public/patient/appointments?scope=${scope}`, tenant), {
      headers: authHeader(),
    });
    return (res.data || []).map(v2ApptToLegacyRow);
  },

  async getMyVisits(tenant?: string): Promise<any[]> {
    const res = await axios.get(url(`/v1/public/patient/visits`, tenant), { headers: authHeader() });
    return (res.data || []).map(v2VisitToLegacyRow);
  },

  async getAppointmentDetail(apptId: string, tenant?: string): Promise<any> {
    const res = await axios.get(url(`/v1/public/patient/appointments/${apptId}`, tenant), {
      headers: authHeader(),
    });
    return res.data;
  },

  async rescheduleAppointment(
    apptId: string,
    scheduledAt: string,
    locationId?: string | null,
    tenant?: string,
  ): Promise<any> {
    const res = await axios.put(
      url(`/v1/public/patient/appointments/${apptId}/reschedule`, tenant),
      { scheduled_at: scheduledAt, location_id: locationId || null },
      { headers: authHeader() },
    );
    return res.data;
  },

  // Visit detail incl. prescription lines + diagnoses (for the Rx download).
  async getVisitDetail(visitId: string, tenant?: string): Promise<any> {
    const res = await axios.get(url(`/v1/public/patient/visits/${visitId}`, tenant), {
      headers: authHeader(),
    });
    return res.data;
  },

  async listDocuments(tenant?: string): Promise<any[]> {
    const res = await axios.get(url(`/v1/public/patient/documents`, tenant), {
      headers: authHeader(),
    });
    return res.data || [];
  },

  async uploadDocument(file: File, kind = "other", tenant?: string): Promise<any> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const res = await axios.post(url(`/v1/public/patient/documents`, tenant), fd, {
      headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  // Download is authed (patient JWT), so a plain <a href> can't carry the token —
  // fetch the bytes as a blob and open them in a new tab.
  async downloadDocument(docId: string, tenant?: string): Promise<void> {
    const res = await axios.get(url(`/v1/public/patient/documents/${docId}/download`, tenant), {
      headers: authHeader(),
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(res.data);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    // Revoke after the new tab has had time to load it.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },

  async cancelAppointment(apptId: string, reason?: string, tenant?: string): Promise<any> {
    const res = await axios.post(
      url(`/v1/public/patient/appointments/${apptId}/cancel`, tenant),
      { reason: reason || undefined },
      { headers: authHeader() },
    );
    return res.data;
  },

  // Validate the patient JWT (used on app load). Rejects on 401/expired.
  async me(tenant?: string): Promise<any> {
    const res = await axios.get(url(`/v1/public/patient/me`, tenant), {
      headers: authHeader(),
    });
    return res.data;
  },

  async logout(tenant?: string): Promise<void> {
    try {
      await axios.delete(url(`/v1/public/patient/logout`, tenant), { headers: authHeader() });
    } catch {
      /* stateless — clearing the token client-side is what matters */
    }
  },
};

export default v2;
